import type { ReactNode } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { copy } from '@/copy'
import { Terminal } from './Terminal'

export type Labelled = { label: string; command: string }

/** Commands labelled by what they do, side by side from 960px, never cut short. */
export function CommandGrid({ commands }: { commands: Labelled[] }) {
  return (
    <div className="grid gap-x-5 gap-y-3 min-[960px]:grid-cols-2">
      {commands.map((c) => (
        <div key={c.command} className="min-w-0">
          <p className="m-0 mb-1.5 text-[13px] font-medium leading-5 text-secondary">{c.label}</p>
          <Terminal command={c.command} />
        </div>
      ))}
    </div>
  )
}

// What to run next, under a list: a heading, one line and the commands.
export function Commands({ heading, line, commands }: { heading: string; line: string; commands: Labelled[] }) {
  return (
    <section className="mt-8" aria-labelledby="commands-heading">
      <h2 id="commands-heading" className="m-0 text-[16px] font-medium leading-6 tracking-[-0.02em]">
        {heading}
      </h2>
      <p className="m-0 mb-3 text-[14px] leading-5 text-secondary">{line}</p>
      <CommandGrid commands={commands} />
    </section>
  )
}

/** An empty list, and everything someone needs to fill it: the steps, in order, each with its command. */
export function Start({ heading, line, children }: { heading: string; line: string; children: ReactNode }) {
  return (
    <section className="card p-6 min-[481px]:p-8" aria-labelledby="start-heading">
      <h2 id="start-heading" className="m-0 text-[22px] font-[450] leading-8 tracking-[-0.035em]">
        {heading}
      </h2>
      <p className="m-0 mb-6 mt-1 text-secondary">{line}</p>
      {children}
    </section>
  )
}

export function Steps({ children }: { children: ReactNode }) {
  return <ol className="m-0 grid list-none gap-6 p-0">{children}</ol>
}

/** One numbered step: its index in the accent (`01`), what to do, and how. */
export function Step({ n, title, line, children }: { n: number; title: string; line: string; children: ReactNode }) {
  return (
    <li className="m-0 grid grid-cols-[32px_minmax(0,1fr)] gap-x-4">
      <span
        className="pt-[5px] font-mono text-[12px] leading-5 text-accent"
        aria-hidden="true"
      >
        {String(n).padStart(2, '0')}
      </span>
      <div className="min-w-0">
        <h3 className="m-0 text-[17px] font-medium leading-8">
          <span className="visually-hidden">{n}. </span>
          {title}
        </h3>
        <p className="m-0 mb-3 text-[15px] leading-[22px] text-secondary">{line}</p>
        {children}
      </div>
    </li>
  )
}

/** Install lives on the site, which knows what is published for each system. */
export function InstallLink() {
  return (
    <a className="action" href={copy.start.installURL} target="_blank" rel="noopener">
      {copy.start.install}
      <ArrowUpRight className="h-[17px] w-[17px]" aria-hidden="true" />
    </a>
  )
}
